
import React, { useEffect, useState } from 'react';
import { Box, TableBody } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  CreateButton,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  ActionButton,
  DeleteIconWrapper
} from './styles';
import CustomPagination from '../../../components/CustomPagination';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import RoleModal from './components/RoleModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import { searchRoles, deleteRole } from '../../../modules/masterdata/actions';
import {
  selectRolesList,
  selectSearchRolesCount,
  selectCreateRoleSuccess,
  selectCreateRoleError,
  selectUpdateRoleSuccess,
  selectUpdateRoleError,
  selectDeleteRoleSuccess,
  selectDeleteRoleError
} from '../../../modules/masterdata/selectors';
import {
  resetCreateRoleState,
  resetUpdateRoleState,
  resetDeleteRoleState
} from '../../../modules/masterdata/reducers';
import { IRole } from '../../../modules/masterdata/types';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import AlertPopup from '../../../components/common/AlertPopup';

const Role: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const roles = useSelector(selectRolesList);
  const totalCount = useSelector(selectSearchRolesCount);

  // Success/Error Selectors
  const createSuccess = useSelector(selectCreateRoleSuccess);
  const createError = useSelector(selectCreateRoleError);
  const updateSuccess = useSelector(selectUpdateRoleSuccess);
  const updateError = useSelector(selectUpdateRoleError);
  const deleteSuccess = useSelector(selectDeleteRoleSuccess);
  const deleteError = useSelector(selectDeleteRoleError);

  const PAGE_SIZE = 10;

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<IRole | null>(null);

  // Confirmation Dialog state
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Alert state
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setActivePage('role');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Role', path: '/master-data/roles' },
    ]);
    setIsShow(true);
    dispatch(searchRoles({ search: '', page: 1, page_size: PAGE_SIZE }));
  }, [setActivePage, setPath, setIsShow, dispatch]);

  const [page, setPage] = useState(1);

  // Handle Notifications and Resets
  useEffect(() => {
    if (createSuccess) {
      setAlert({ message: 'Role created successfully', severity: 'success' });
      dispatch(searchRoles({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetCreateRoleState());
    }
    if (createError) {
      setAlert({ message: createError, severity: 'error' });
      dispatch(resetCreateRoleState());
    }

    if (updateSuccess) {
      setAlert({ message: 'Role updated successfully', severity: 'success' });
      dispatch(searchRoles({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetUpdateRoleState());
    }
    if (updateError) {
      setAlert({ message: updateError, severity: 'error' });
      dispatch(resetUpdateRoleState());
    }

    if (deleteSuccess) {
      setAlert({ message: 'Role deleted successfully', severity: 'success' });
      dispatch(searchRoles({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetDeleteRoleState());
    }
    if (deleteError) {
      setAlert({ message: deleteError, severity: 'error' });
      dispatch(resetDeleteRoleState());
    }
  }, [createSuccess, createError, updateSuccess, updateError, deleteSuccess, deleteError, dispatch, page]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    dispatch(searchRoles({ search: '', page: newPage, page_size: PAGE_SIZE }));
  };

  const handleCreateClick = () => {
    setSelectedRole(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (role: IRole) => {
    setSelectedRole(role);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await dispatch(deleteRole(deleteId));
      setIsConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const totalPages = Math.ceil((totalCount || 0) / PAGE_SIZE);

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Manage Roles</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={handleCreateClick}>
            Create Role
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {roles && roles.length > 0 ? (
                roles.map((role: IRole, index: number) => (
                  <StyledTableRow key={role.role_id}>
                    <StyledTableCell>{role.role_id}</StyledTableCell>
                    <StyledTableCell>{role.role_name}</StyledTableCell>
                    <StyledTableCell>{role.role_description}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ActionButton onClick={() => handleEditClick(role)}>
                          <EditIcon fontSize="small" />
                        </ActionButton>
                        <DeleteIconWrapper onClick={() => handleDeleteClick(role.role_id)}>
                          <DeleteIcon fontSize="small" />
                        </DeleteIconWrapper>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={4} align="center">No roles found.</StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <CustomPagination
            totalPages={totalPages > 0 ? totalPages : 1}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </MainContainer>

      <RoleModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        role={selectedRole}
      />

      <ConfirmationDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Role"
        message="Are you sure you want to delete this role?"
      />
    </Box>
  );
};

export default Role;
